import type { ReactElement } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Icon, tokens, Tooltip } from '@hotjar/hotsauce';
import type { IconSetDefinitions } from '@hotjar/hotsauce/build/components/Icon/icons';
import { ListItem } from '@hotjar/webapp/components/DropdownMenu';
import { useTranslationWithPrefix } from '@hotjar/translations';

interface SiteListActionProps {
  icon: keyof IconSetDefinitions;
  text: string;
  tooltipText: string;
  isAdmin: boolean;
  isMobile?: boolean;
  isDisabled?: boolean;
  onClick(): void;
}

const stylesDisabled = {
  color: tokens.color.text_disabled,
  cursor: 'not-allowed',
};

const stylesEnabled = css`
  color: ${tokens.color.text_light};
  cursor: pointer;
  &:hover,
  &:active {
    background-color: ${tokens.color.neutral_200};
  }
`;

const S = {
  Icon: styled.span<Pick<SiteListActionProps, 'isDisabled'>>`
    padding: 0 ${tokens.spacer.x0} ${tokens.spacer.x0};
    border-radius: ${tokens.radius.m};
    outline: none;
    ${({ isDisabled }) => (isDisabled ? stylesDisabled : stylesEnabled)};
  `,
};

export const SiteListAction = ({
  icon,
  text,
  tooltipText,
  isAdmin,
  isMobile,
  isDisabled = false,
  onClick,
}: SiteListActionProps): ReactElement => {
  const { t } = useTranslationWithPrefix('account:sites-and-organizations.site.actions');

  const disabled = isDisabled || !isAdmin;
  const tooltip = isAdmin ? tooltipText : t('not-admin');

  return isMobile ? (
    <ListItem onClick={disabled ? undefined : onClick} disabled={disabled}>
      {text}
    </ListItem>
  ) : (
    <Tooltip placement="top" text={tooltip} isDisabled={!tooltip}>
      <S.Icon
        role="button"
        tabIndex={0}
        onClick={disabled ? undefined : onClick}
        isDisabled={disabled}
        aria-label={text}
      >
        <Icon icon={icon} description={text} />
      </S.Icon>
    </Tooltip>
  );
};
